import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";

const STYLES = ["kame", "domain", "burst"];

function prefersReducedMotion() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function shakeScreen(duration = 500) {
  const el = document.documentElement;
  el.classList.remove("glnc-shake");
  void el.offsetWidth;
  el.classList.add("glnc-shake");
  window.setTimeout(() => el.classList.remove("glnc-shake"), duration + 20);
}

export default function NerdCorner() {
  const [phase, setPhase] = useState("idle"); // idle | playing
  const [style, setStyle] = useState(null);
  const [reduced, setReduced] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setReduced(prefersReducedMotion());
  }, []);

  const trigger = useCallback(() => {
    if (phase === "playing") return;
    if (reduced) {
      navigate("/nerd");
      return;
    }
    const chosen = STYLES[Math.floor(Math.random() * STYLES.length)];
    setStyle(chosen);
    setPhase("playing");
    // Screen shake near the impact
    window.setTimeout(() => shakeScreen(520), 620);
    // Navigate after the blast peaks
    window.setTimeout(() => {
      setPhase("idle");
      navigate("/nerd");
    }, 1050);
  }, [phase, reduced, navigate]);

  return (
    <>
      {/* GLNC corner button */}
      <motion.button
        data-testid="glnc-corner-btn"
        onClick={trigger}
        aria-label="GLNC — Nerd Corner"
        initial={{ opacity: 0, scale: 0.7, rotate: -8 }}
        animate={{ opacity: 1, scale: 1, rotate: 0 }}
        transition={{ delay: 2, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        whileHover={{ scale: 1.08, rotate: -3 }}
        whileTap={{ scale: 0.92, rotate: 3 }}
        className="fixed z-[95] bottom-4 right-4 md:bottom-5 md:right-5 group select-none"
      >
        <span className="glnc-pulse absolute inset-0 rounded-sm" aria-hidden />
        <span className="relative block bg-[#FFDE00] text-black font-mono-tech font-bold px-3 py-2 md:px-4 md:py-2.5 text-xs md:text-sm tracking-widest border-2 border-black shadow-[3px_3px_0_0_#000] group-hover:shadow-[5px_5px_0_0_#000] group-hover:-translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-200">
          GLNC
        </span>
      </motion.button>

      {/* Blast layer */}
      <AnimatePresence>
        {phase === "playing" && (
          <motion.div
            data-testid="glnc-blast"
            key="glnc-blast"
            initial={{ opacity: 1 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[110] pointer-events-none overflow-hidden"
          >
            {style === "kame" && <KameBlast />}
            {style === "domain" && <DomainBlast />}
            {style === "burst" && <ImpactBurst />}
            <motion.div
              className="absolute inset-0 bg-white mix-blend-screen"
              initial={{ opacity: 0 }}
              animate={{ opacity: [0, 0, 1, 0] }}
              transition={{ duration: 1.0, times: [0, 0.55, 0.75, 1], ease: "easeOut" }}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

/* ---------- Blast style A: Kamehameha-inspired beam ---------- */
function KameBlast() {
  return (
    <div className="absolute inset-0" data-testid="blast-kame">
      <motion.div
        className="absolute w-24 h-24 rounded-full bg-[#7ee7ff] blur-md"
        style={{ top: "22%", left: "10%" }}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: [0, 1.5, 1.2], opacity: [0, 1, 1] }}
        transition={{ duration: 0.5, ease: "easeOut" }}
      />
      <motion.div
        className="absolute w-16 h-16 rounded-full bg-white"
        style={{ top: "23%", left: "12%" }}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: [0, 1.2, 1], opacity: [0, 1, 0.9] }}
        transition={{ duration: 0.5, ease: "easeOut" }}
      />
      <motion.div
        className="absolute origin-left"
        style={{
          top: "31%",
          left: "18%",
          height: "58px",
          width: "0%",
          transform: "rotate(22deg)",
          background:
            "linear-gradient(90deg, rgba(126,231,255,0) 0%, #7ee7ff 20%, #ffffff 55%, #7ee7ff 85%, rgba(126,231,255,0) 100%)",
          filter: "blur(4px)",
          boxShadow: "0 0 60px 20px rgba(126,231,255,0.6)",
        }}
        initial={{ width: "0%" }}
        animate={{ width: ["0%", "110%", "110%"] }}
        transition={{ duration: 0.7, delay: 0.3, ease: "easeOut", times: [0, 0.7, 1] }}
      />
      <motion.div
        className="absolute bottom-6 right-6 w-8 h-8 rounded-full border-4 border-white"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: [0, 12, 20], opacity: [0, 1, 0] }}
        transition={{ duration: 0.6, delay: 0.7, ease: "easeOut" }}
      />
    </div>
  );
}

/* ---------- Blast style B: Domain-inspired dark rings + cross-slashes ---------- */
function DomainBlast() {
  return (
    <div className="absolute inset-0" data-testid="blast-domain">
      <motion.div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(circle at 50% 50%, rgba(30,0,60,0.9) 0%, rgba(0,0,0,0.85) 40%, rgba(0,0,0,0) 70%)",
        }}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: [0, 1.4, 1.4], opacity: [0, 1, 0.9] }}
        transition={{ duration: 0.7, ease: "easeOut" }}
      />
      {[0, 0.12, 0.24].map((d, i) => (
        <motion.div
          key={i}
          className="absolute left-1/2 top-1/2 rounded-full border-2 border-[#c084fc]"
          style={{ width: 40, height: 40, marginLeft: -20, marginTop: -20 }}
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: [0, 30], opacity: [1, 0] }}
          transition={{ duration: 1, delay: d, ease: "easeOut" }}
        />
      ))}
      <motion.div
        className="absolute left-1/2 top-1/2 h-[3px] w-[140vmax] bg-white"
        style={{ transform: "translate(-50%,-50%) rotate(35deg)", boxShadow: "0 0 24px #ffffff" }}
        initial={{ scaleX: 0, opacity: 0 }}
        animate={{ scaleX: [0, 1, 1], opacity: [0, 1, 0] }}
        transition={{ duration: 0.5, delay: 0.4, ease: "easeOut" }}
      />
      <motion.div
        className="absolute left-1/2 top-1/2 h-[3px] w-[140vmax] bg-white"
        style={{ transform: "translate(-50%,-50%) rotate(-35deg)", boxShadow: "0 0 24px #ffffff" }}
        initial={{ scaleX: 0, opacity: 0 }}
        animate={{ scaleX: [0, 1, 1], opacity: [0, 1, 0] }}
        transition={{ duration: 0.5, delay: 0.55, ease: "easeOut" }}
      />
    </div>
  );
}

/* ---------- Blast style C: Radial impact burst with speed lines ---------- */
function ImpactBurst() {
  const rays = Array.from({ length: 14 });
  return (
    <div className="absolute inset-0" data-testid="blast-burst">
      {Array.from({ length: 22 }).map((_, i) => {
        const angle = (i / 22) * 360;
        return (
          <motion.div
            key={i}
            className="absolute left-1/2 top-1/2 origin-left h-[2px] bg-white"
            style={{ transform: `rotate(${angle}deg)`, width: "45vmax" }}
            initial={{ scaleX: 0, opacity: 0.9 }}
            animate={{ scaleX: [0, 1, 0], opacity: [0.9, 0.9, 0] }}
            transition={{ duration: 0.5, delay: 0.1 + (i % 4) * 0.03, ease: "easeOut" }}
          />
        );
      })}
      <div className="absolute left-1/2 top-1/2">
        {rays.map((_, i) => {
          const angle = (i / rays.length) * 360;
          return (
            <motion.div
              key={i}
              className="absolute h-[6px] bg-[#FFDE00]"
              style={{
                left: 0,
                top: -3,
                transformOrigin: "0% 50%",
                transform: `rotate(${angle}deg)`,
                width: "40vmax",
                boxShadow: "0 0 20px #FFDE00",
              }}
              initial={{ scaleX: 0, opacity: 0 }}
              animate={{ scaleX: [0, 1, 1.05], opacity: [0, 1, 0] }}
              transition={{ duration: 0.6, delay: 0.35, ease: "easeOut" }}
            />
          );
        })}
      </div>
      <motion.div
        className="absolute left-1/2 top-1/2 rounded-full border-4 border-[#FFDE00]"
        style={{ width: 60, height: 60, marginLeft: -30, marginTop: -30 }}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: [0, 25], opacity: [1, 0] }}
        transition={{ duration: 0.8, delay: 0.5, ease: "easeOut" }}
      />
    </div>
  );
}
